import React, { useMemo, useRef, useState, useEffect, useSyncExternalStore, useCallback } from 'react';
import { getAbsolutePlayerTimePure } from '@goldcast/player-config/src/utils/timeUtils';
import { useDragElement } from './hooks/useDragElement';
import { MIN_SEGMENT_WIDTH } from './timeUtils';
import { zoomLevelStore } from './store/zoomLevel';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

export default function CuePoint({
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  speakerTimeLineFixedWidthContainer
}: {
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimeLineFixedWidthContainer: React.RefObject<HTMLDivElement>;
}) {
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { clipData, fps } = useClipsContext();
  const cuePointRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const { seekTo } = useRemotionPlayerSeek();
  const [shouldScrollToCurrentTime, setShouldScrollToCurrentTime] = useState<boolean>(true);

  const scrollToCurrentTime = useCallback(() => {
    const cuePointElement = cuePointRef.current;
    if (cuePointElement) {
      cuePointElement.scrollIntoView({
        behavior: 'instant',
        block: 'start',
        inline: 'center'
      });
    }
  }, []);

  useEffect(() => {
    if (shouldScrollToCurrentTime) {
      EventBus.on(CustomEvents.ZoomValueChangedFromRangeSlider, scrollToCurrentTime);
      return () => {
        EventBus.off(CustomEvents.ZoomValueChangedFromRangeSlider, scrollToCurrentTime);
      };
    }
  }, [shouldScrollToCurrentTime, scrollToCurrentTime]);

  useEffect(() => {
    const checkVisibility = () => {
      const cuePointElement = cuePointRef.current;
      const speakerTimelineFixedWidthElement = speakerTimeLineFixedWidthContainer?.current;
      if (cuePointElement && speakerTimelineFixedWidthElement) {
        const rect = cuePointElement.getBoundingClientRect();
        const containerRect = speakerTimelineFixedWidthElement.getBoundingClientRect();
        const isVisible = rect.left >= containerRect.left && rect.right <= containerRect.right;
        if (!isVisible) {
          scrollToCurrentTime();
        }
      }
    };
    if (shouldScrollToCurrentTime) {
      checkVisibility();
    }
  }, [currentTime, scrollToCurrentTime]);

  const totalDurationInFrames = getClipTotalDurationInFrames(clipData);

  const { onMouseDown: onCuePointDrag } = useDragElement({
    setDraggingElement: value => setIsDragging(!!value),
    setDraggingElementOffset: setDragOffset,
    onDragEnd: offset => {
      const newTime = currentTime + offset;
      const maxTime = totalDurationInFrames / fps;
      const minTime = 0;
      const clampedTime = Math.max(minTime, Math.min(maxTime, newTime));
      seekTo(clampedTime);
    },
    validDropZoneRef: newTimelineContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    fps
  });

  const { zoomLevel } = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);

  const leftPosition = useMemo(() => {
    const absolutePlayerTime =
      getAbsolutePlayerTimePure({
        time: currentTime,
        clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }) - compositeSegmentDragStart;
    const maxAbsolutePlayerTime =
      getAbsolutePlayerTimePure({
        time: totalDurationInFrames / fps,
        clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }) +
      compositeSegmentDragEnd -
      compositeSegmentDragStart;
    const minAbsolutePlayerTime = 0;
    return Math.min(Math.max(absolutePlayerTime, minAbsolutePlayerTime), maxAbsolutePlayerTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clipData,
    compositeSegmentDragEnd,
    compositeSegmentDragStart,
    currentTime,
    zoomLevel,
    fps,
    totalDurationInFrames
  ]);

  const maxNegativeOffset = -leftPosition;
  const maxPositiveOffset =
    getAbsolutePlayerTimePure({
      time: totalDurationInFrames / fps,
      clipData,
      minSegmentWidth: MIN_SEGMENT_WIDTH,
      zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }) - leftPosition;

  useEffect(() => {
    if (!speakerTimelineContainerRef.current) {
      return;
    }
    const speakerTimelineContainerElement = speakerTimelineContainerRef.current;
    const checkIfMouseEnter = () => {
      setShouldScrollToCurrentTime(false);
    };

    const checkIfMouseLeave = () => {
      setShouldScrollToCurrentTime(true);
    };

    speakerTimelineContainerElement.addEventListener('mouseenter', checkIfMouseEnter);
    speakerTimelineContainerElement.addEventListener('mouseleave', checkIfMouseLeave);

    return () => {
      if (speakerTimelineContainerElement) {
        speakerTimelineContainerElement.removeEventListener('mouseenter', checkIfMouseEnter);
        speakerTimelineContainerElement.removeEventListener('mouseleave', checkIfMouseLeave);
      }
    };
  }, [speakerTimelineContainerRef]);

  return (
    <div
      className="pointer-events-none absolute top-0 z-20 h-full w-px"
      data-testid="timeline-cue-point"
      ref={cuePointRef}
      style={{
        left: `calc(${fps * (leftPosition + (isDragging ? dragOffset : 0))}px * var(--timeline-zoom))`
      }}
    >
      <div className="pointer-events-none -ml-0.5 h-full w-0.5 bg-[#10172A]">
        <div className="pointer-events-auto sticky left-1/2 top-[-8px] z-10 text-[#10172A] hover:cursor-ew-resize">
          <Icon
            className="size-[18px] translate-x-[-43%] translate-y-[-56%] rotate-90"
            name="IconLabelFilled"
            onMouseDown={(e: React.MouseEvent<SVGSVGElement>) => {
              e.preventDefault();
              e.stopPropagation();
              const syntheticEvent = e as unknown as React.MouseEvent<HTMLDivElement>;
              onCuePointDrag(syntheticEvent, 'cue-point', maxPositiveOffset, maxNegativeOffset);
            }}
          />
        </div>
      </div>
    </div>
  );
}
